import { Component } from '@theme/component';
import { DialogComponent } from '@theme/dialog';

const selectors = {
  DIALOG: 'dialog-component',
  LIVE_CHAT_TAB: '#inside_liveChatTab',
};

export class LiveChat extends Component {
  #liveChatTab = /** @type {HTMLElement | null} */ (null);
  #abortController = new AbortController();
  #liveChatInterval = /** @type {ReturnType<typeof setInterval> | null} */ (null);
  // True while waiting for Powerfront tab after user clicked — blocks duplicate clicks.
  #pendingOpen = false;

  get #button() {
    return /** @type {HTMLButtonElement} */ (this.refs.button);
  }

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;

    this.#button.addEventListener('click', this.#openLiveChat, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.#clearLiveChatInterval();
  }

  #clearLiveChatInterval() {
    if (!this.#liveChatInterval) return;

    clearInterval(this.#liveChatInterval);
    this.#liveChatInterval = null;
  }

  #resetPending() {
    this.#pendingOpen = false;
    delete this.#button.dataset.loading;
  }

  // Detect Powerfront's tab in the DOM. If found and pending, open the widget.
  #handleLiveChatInit() {
    if (this.#liveChatTab) return;

    const tab = document.querySelector(selectors.LIVE_CHAT_TAB);

    if (!(tab instanceof HTMLElement) || !tab.checkVisibility()) return;

    this.#liveChatTab = tab;
    this.#clearLiveChatInterval();

    if (!this.#pendingOpen) return;

    this.#openWidget();
  }

  #openLiveChat = () => {
    if (this.#pendingOpen) return;

    if (this.#liveChatTab) {
      this.#openWidget();
      return;
    }

    this.#button.dataset.loading = '';
    this.#pendingOpen = true;

    // Poll until Powerfront tab appears, then open the widget.
    const deadline = Date.now() + 15_000;

    this.#liveChatInterval = setInterval(() => {
      this.#handleLiveChatInit();

      if (this.#liveChatTab) return;

      if (Date.now() >= deadline) {
        this.#clearLiveChatInterval();
        this.#resetPending();
        console.error('LiveChat: Powerfront tab not found');
      }
    }, 500);
  };

  #openWidget() {
    document.querySelectorAll(selectors.DIALOG).forEach((dialog) => {
      if (dialog instanceof DialogComponent) dialog.closeDialog();
    });

    this.#liveChatTab?.click();
    this.#resetPending();
  }
}

if (!customElements.get('live-chat')) {
  customElements.define('live-chat', LiveChat);
}
